import { useCallback, useEffect, useRef, useState } from "react";
import api, { streamPost } from "@/lib/api";
import StructuredAnswer from "@/components/StructuredAnswer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { FileText, Loader2, Paperclip, Scale, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";

const STARTERS = [
  "Summarize the authority relevant to this matter.",
  "What deadlines or procedural risks should I check?",
  "Find the strongest authority for my position.",
  "Draft the next filing using only verified authority.",
];

const ALLOWED_UPLOAD_TYPES = ["application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "text/plain", "image/jpeg", "image/png"];

export default function LeegoCopilot({ matterContext = "", topic = null, jurisdictionScope = "federal", stateCode = "", prompt = null, onPromptConsumed }) {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [mode, setMode] = useState("research");
  const [streaming, setStreaming] = useState(false);
  const [files, setFiles] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [handoff, setHandoff] = useState(null);
  const previousScope = useRef(jurisdictionScope);
  const previousState = useRef(stateCode);
  const scopeLabel = jurisdictionScope === "federal" ? "Federal" : "State / Local";
  const fileRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const ensureSession = useCallback(async () => {
    if (sessionId) return sessionId;
    const r = await api.post("/chat/sessions", {
      title: "Leego Beego workspace", topic, jurisdiction_scope: jurisdictionScope,
      state: jurisdictionScope === "state_local" ? (stateCode || null) : null,
    });
    setSessionId(r.data.session_id);
    return r.data.session_id;
  }, [sessionId, topic, jurisdictionScope, stateCode]);

  useEffect(() => {
    if (previousScope.current === jurisdictionScope) return;
    previousScope.current = jurisdictionScope;
    setFiles([]);
    if (!sessionId) {
      setMessages([]);
      setHandoff(null);
      return;
    }
    let cancelled = false;
    setStreaming(true);
    api.post(`/chat/sessions/${sessionId}/switch-scope`, { jurisdiction_scope: jurisdictionScope })
      .then(async (response) => {
        if (cancelled) return;
        const next = response.data.session;
        setSessionId(next.session_id);
        const saved = await api.get(`/chat/sessions/${next.session_id}/messages`);
        if (cancelled) return;
        setMessages(saved.data.messages || []);
        setHandoff({
          summary: response.data.handoff_summary,
          documentCount: (response.data.documents || []).length,
        });
        toast.success(`Switched to ${jurisdictionScope === "federal" ? "Federal" : "State / Local"}; prior work was preserved.`);
      })
      .catch((error) => toast.error(error.response?.data?.detail || "Could not switch legal jurisdiction."))
      .finally(() => { if (!cancelled) setStreaming(false); });
    return () => { cancelled = true; };
  }, [jurisdictionScope, sessionId]);

  // Refining which state applies within the same state_local session doesn't need
  // the full scope-switch/handoff flow above — just update the session in place.
  useEffect(() => {
    if (previousState.current === stateCode) return;
    previousState.current = stateCode;
    if (!sessionId || jurisdictionScope !== "state_local") return;
    api.patch(`/chat/sessions/${sessionId}`, { state: stateCode || null }).catch(() => {});
  }, [stateCode, sessionId, jurisdictionScope]);

  const send = useCallback(async (rawText) => {
    const question = (rawText ?? input).trim();
    if ((!question && files.length === 0) || streaming) return;
    const sid = await ensureSession();
    const attachments = files;
    const display = question || `Review my ${attachments.length} attached document(s).`;
    const contextPrefix = matterContext
      ? `Workspace context (use only when relevant):\n${matterContext}\n\nUser question:\n`
      : "";
    const requestText = `${contextPrefix}${question}`.trim();

    setInput("");
    setFiles([]);
    setMessages((m) => [...m,
      { role: "user", content: display },
      { role: "assistant", content: "", sources: [] },
    ]);
    setStreaming(true);

    try {
      await streamPost(
        "/chat/message",
        {
          session_id: sid,
          text: requestText,
          mode,
          attachments: attachments.map((f) => ({ name: f.name, mime: f.mime, data_base64: f.data_base64 })),
        },
        (delta) => setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          if (last?.role === "assistant") copy[copy.length - 1] = { ...last, content: last.content + delta };
          return copy;
        }),
        (meta) => {
          setMessages((m) => {
            const copy = [...m];
            const last = copy[copy.length - 1];
            if (last?.role === "assistant") copy[copy.length - 1] = { ...last, sources: meta.sources || [] };
            return copy;
          });
          // Leego may upgrade a plain-language request ("draft me a complaint...") into
          // Draft mode server-side rather than requiring the toggle click below — sync
          // the toggle so the visitor sees which mode actually answered them, and so
          // their next follow-up message stays in Draft mode too.
          if (meta.auto_switched_to_draft) {
            setMode("draft");
            toast.info("Leego switched to Draft mode to produce a formatted document for this request.");
          }
        },
      );
    } catch (err) {
      toast.error(err.message || "Leego Beego could not answer that request.");
      setMessages((m) => m.slice(0, -1));
    } finally {
      setStreaming(false);
    }
  }, [ensureSession, files, input, matterContext, mode, streaming]);

  useEffect(() => {
    if (!prompt || streaming) return;
    send(prompt);
    onPromptConsumed?.();
  }, [prompt, streaming, send, onPromptConsumed]);

  const loadFiles = async (fileList) => {
    const picked = Array.from(fileList || []).slice(0, Math.max(0, 4 - files.length));
    const accepted = picked.filter((f) => ALLOWED_UPLOAD_TYPES.includes(f.type));
    if (accepted.length !== picked.length) toast.error("Leego Beego accepts PDF, Word (.docx), text, JPG and PNG files.");
    const loaded = await Promise.all(accepted.map((f) => new Promise((resolve) => {
      if (f.size > 12 * 1024 * 1024) { toast.error(`${f.name} is over the 12 MB limit.`); return resolve(null); }
      const reader = new FileReader();
      reader.onload = () => resolve({ name: f.name, mime: f.type, data_base64: String(reader.result).split(",")[1] });
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(f);
    })));
    setFiles((prev) => [...prev, ...loaded.filter(Boolean)]);
    if (fileRef.current) fileRef.current.value = "";
  };

  if (collapsed) {
    return (
      <aside className="w-14 shrink-0 border-l border-border bg-[#0B1120] flex flex-col items-center py-4">
        <button onClick={() => setCollapsed(false)} className="w-9 h-9 border border-primary/40 bg-primary/10 flex items-center justify-center" title="Open Leego Beego">
          <Scale className="w-4 h-4 text-primary" />
        </button>
        <span className="mt-3 text-[10px] font-mono-tech text-muted-foreground [writing-mode:vertical-rl]">LEEGO AI</span>
      </aside>
    );
  }

  return (
    <aside className="w-[390px] max-w-[42vw] shrink-0 border-l border-border bg-[#0B1120] flex flex-col" data-testid="leego-copilot">
      <div className="px-4 py-3 border-b border-border flex items-center gap-3">
        <div className="w-8 h-8 border border-primary/40 bg-primary/10 flex items-center justify-center">
          <Scale className="w-4 h-4 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">Leego Beego</div>
          <div className="flex items-center gap-2"><span className="text-[10px] text-muted-foreground font-mono-tech">LEGAL RESEARCH COPILOT</span><span className="text-[9px] font-semibold border border-primary/50 bg-primary/10 text-primary px-1.5 py-0.5">{scopeLabel.toUpperCase()}</span></div>
        </div>
        <button onClick={() => setCollapsed(true)} className="text-xs text-muted-foreground hover:text-primary">Collapse</button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4">
        {handoff && <div className="mb-4 border border-primary/30 bg-primary/5 p-3">
          <div className="text-[10px] font-semibold text-primary">{scopeLabel.toUpperCase()} WORKSPACE</div>
          <p className="text-[11px] text-muted-foreground mt-1">Prior conversation saved. A facts-only case summary and {handoff.documentCount} saved document(s) are available here. Legal conclusions will be researched again for this jurisdiction.</p>
        </div>}
        {messages.length === 0 ? (
          <div className="space-y-4">
            <div className="border border-primary/20 bg-primary/5 p-3">
              <div className="flex items-center gap-2 text-primary text-xs font-semibold"><Sparkles className="w-3.5 h-3.5" /> Ready beside your work</div>
              <p className="text-xs text-muted-foreground mt-2 leading-relaxed">Ask while you research. Leego Beego uses HardKnockLegal authority and the current workspace context before model fallback.</p>
            </div>
            <div className="space-y-2">
              {STARTERS.map((s) => (
                <button key={s} onClick={() => send(s)} className="w-full text-left text-xs border border-border hover:border-primary/50 px-3 py-2.5 transition-colors">{s}</button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {messages.map((m, i) => m.role === "user" ? (
              <div key={i} className="flex justify-end"><div className="max-w-[90%] bg-secondary border border-border px-3 py-2 text-xs whitespace-pre-wrap">{m.content}</div></div>
            ) : (
              <div key={i} className="flex gap-2">
                <div className="w-7 h-7 shrink-0 border border-primary/40 bg-primary/10 flex items-center justify-center"><Scale className="w-3.5 h-3.5 text-primary" /></div>
                <div className="min-w-0 flex-1 text-sm">
                  {m.content ? <StructuredAnswer content={m.content} sources={m.sources || []} /> : <div className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking authority…</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-border p-3 bg-[#0A0F1B]">
        <div className="flex items-center gap-1 mb-2">
          <button onClick={() => setMode("research")} className={`text-[10px] px-2 py-1 border ${mode === "research" ? "border-primary/50 text-primary bg-primary/10" : "border-border text-muted-foreground"}`}>Research</button>
          <button onClick={() => setMode("draft")} className={`text-[10px] px-2 py-1 border ${mode === "draft" ? "border-primary/50 text-primary bg-primary/10" : "border-border text-muted-foreground"}`}>Draft</button>
          <span className="ml-auto text-[9px] text-muted-foreground">Verify before filing</span>
        </div>
        {files.length > 0 && <div className="mb-2 flex flex-wrap gap-1">{files.map((f, i) => <span key={`${f.name}-${i}`} className="text-[9px] border border-border px-1.5 py-1 inline-flex items-center gap-1"><FileText className="w-2.5 h-2.5" />{f.name}</span>)}</div>}
        <div className="flex items-end border border-border bg-card focus-within:border-primary/50">
          <input ref={fileRef} type="file" multiple accept=".pdf,.docx,.txt,.jpg,.jpeg,.png,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,image/jpeg,image/png" className="hidden" onChange={(e) => loadFiles(e.target.files)} />
          <button onClick={() => fileRef.current?.click()} className="w-9 h-10 flex items-center justify-center text-muted-foreground hover:text-primary"><Paperclip className="w-4 h-4" /></button>
          <Textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder={`Ask Leego Beego about the ${scopeLabel.toLowerCase()} matter…`} className="border-0 bg-transparent resize-none min-h-[44px] max-h-32 text-xs focus-visible:ring-0" rows={1} />
          <Button onClick={() => send()} disabled={streaming || (!input.trim() && files.length === 0)} className="m-1 w-9 h-9 p-0 rounded-none"><Send className="w-3.5 h-3.5" /></Button>
        </div>
        <p className="text-[9px] text-muted-foreground text-center mt-2">Legal research assistance, not legal advice.</p>
      </div>
    </aside>
  );
}
